// SystemStatusChip — sidebar bottom chip showing aggregate daemon health.
//
// Replaces the W1-era hardcoded "All systems operational" string, which
// lied any time a daemon was down. Aggregates the same heartbeat rows
// the /settings ConnectedServicesCard and /agents page already render —
// no new query, no new substrate.
//
// Four rendered states (alive / asleep / warning / alert) (mirror the classifier tiers in
// @reverra/shared/heartbeat/read.ts):
//   - all alive               → green PulseDot + "All systems operational"
//   - any warning (stale)     → amber + "N service(s) stale"
//   - any alert (stopped)     → red   + "N service(s) stopped"
//   - 0 heartbeats reporting  → amber + "No heartbeats reporting"
//   - loading first fetch     → amber + "Checking systems…"
//   - query error             → amber + "Status unavailable"
//
// "0 heartbeats" is treated as not-green on purpose: it means we don't
// know, and a green "operational" indicator should never lie. The
// previous static chip violated this on every fresh-dev boot.
//
// Click target: the whole chip is a Link to /agents — clicking the
// status indicator should jump to the page that explains it.
//
// Screen field audit (CLAUDE.md §19):
//   chip text     ← computed from rows + classifyHeartbeat()
//   tooltip       ← worst-status row names, top 3
//   PulseDot hue  ← STATUS_DOT_COLOR keyed by aggregated tier

import { useEffect, useMemo, useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { Link } from 'react-router-dom';
import {
  captureChannelLabel,
  classifyHeartbeat,
  type HeartbeatRow,
  type HeartbeatStatus,
  readAllHeartbeats,
} from '@reverra/shared/heartbeat/read';
import PulseDot from '@/components/shared/PulseDot';
import { supabase } from '@/lib/supabase';

type ChipTier = 'alive' | 'asleep' | 'warning' | 'alert' | 'unknown';

// 'asleep' ranks above alive so the chip says something, but below warning: a
// laptop with its lid shut is the system working as designed, not a fault.
const STATUS_RANK: Record<HeartbeatStatus, number> = {
  alive: 0,
  asleep: 1,
  warning: 2,
  alert: 3,
};

const DOT_COLOR: Record<ChipTier, 'green' | 'amber' | 'red'> = {
  alive: 'green',
  // Green, deliberately: nothing is wrong and nothing needs doing. Amber would
  // read as "act on me" for a laptop that is simply shut.
  asleep: 'green',
  warning: 'amber',
  alert: 'red',
  unknown: 'amber',
};

const TEXT_CLASS: Record<ChipTier, string> = {
  alive: 'text-forest',
  asleep: 'text-muted-foreground',
  warning: 'text-attention',
  alert: 'text-alert',
  unknown: 'text-muted-foreground',
};

const BG_CLASS: Record<ChipTier, string> = {
  alive: 'bg-forest-soft/50',
  asleep: 'bg-secondary/50',
  warning: 'bg-attention/10',
  alert: 'bg-alert/10',
  unknown: 'bg-secondary/50',
};

type Aggregate = {
  tier: ChipTier;
  label: string;
  worstNames: string[];
};

export function aggregateHeartbeats(rows: HeartbeatRow[], now: Date): Aggregate {
  const withPayload = rows.filter((r) => r.payload !== null);
  if (rows.length === 0 || withPayload.length === 0) {
    return { tier: 'unknown', label: 'No heartbeats reporting', worstNames: [] };
  }

  let worst: HeartbeatStatus = 'alive';
  const perRow = withPayload.map((row) => {
    // daemonName lets a capture daemon that is merely asleep classify as
    // 'asleep' rather than 'alert' — otherwise every morning the chip reads
    // "2 services stopped" while capture is perfectly healthy.
    const status = classifyHeartbeat(row.payload!, now, { daemonName: row.daemon_name }).status;
    if (STATUS_RANK[status] > STATUS_RANK[worst]) worst = status;
    return { name: row.daemon_name, status };
  });

  if (worst === 'alive') {
    return { tier: 'alive', label: 'All systems operational', worstNames: [] };
  }

  const worstRows = perRow
    .filter((r) => r.status === worst)
    .map((r) => captureChannelLabel(r.name))
    .sort();
  const count = worstRows.length;
  const noun = count === 1 ? 'channel' : 'channels';
  if (worst === 'asleep') {
    return {
      tier: 'asleep',
      label: `${count} ${noun} asleep — Mac shut`,
      worstNames: worstRows.slice(0, 3),
    };
  }
  const serviceNoun = count === 1 ? 'service' : 'services';
  const verb = worst === 'warning' ? 'stale' : 'stopped';
  return {
    tier: worst,
    label: `${count} ${serviceNoun} ${verb}`,
    worstNames: worstRows.slice(0, 3),
  };
}

export default function SystemStatusChip(): JSX.Element {
  // `now` ticks every minute so staleness re-classifies without a refetch.
  // 60s matches the Settings page's interval — keeps cache identity stable
  // between the two consumers.
  const [now, setNow] = useState<Date>(() => new Date());
  useEffect(() => {
    const id = window.setInterval(() => setNow(new Date()), 60_000);
    return () => window.clearInterval(id);
  }, []);

  const heartbeatsQ = useQuery({
    queryKey: ['heartbeats'],
    queryFn: () => readAllHeartbeats(supabase),
    refetchInterval: 30_000,
    retry: 0,
  });

  const aggregate = useMemo<Aggregate>(() => {
    if (heartbeatsQ.isLoading) {
      return { tier: 'unknown', label: 'Checking systems…', worstNames: [] };
    }
    if (heartbeatsQ.isError) {
      return { tier: 'unknown', label: 'Status unavailable', worstNames: [] };
    }
    return aggregateHeartbeats(heartbeatsQ.data ?? [], now);
  }, [heartbeatsQ.data, heartbeatsQ.isError, heartbeatsQ.isLoading, now]);

  const tooltip =
    aggregate.worstNames.length > 0
      ? `Channels: ${aggregate.worstNames.join(', ')}${
          aggregate.worstNames.length >= 3 ? ' (…)' : ''
        }`
      : aggregate.label;

  return (
    <Link
      to="/agents"
      title={tooltip}
      aria-label={`${aggregate.label} — open Agents`}
      className={`flex items-center gap-2 px-3 py-2 rounded-lg ${BG_CLASS[aggregate.tier]} transition-colors hover:bg-secondary`}
    >
      <PulseDot color={DOT_COLOR[aggregate.tier]} size="sm" />
      <span className={`text-[11px] font-medium ${TEXT_CLASS[aggregate.tier]}`}>
        {aggregate.label}
      </span>
    </Link>
  );
}
